import React from "react";
import Modal from "rsuite/Modal";
import Button from "rsuite/Button";
import type { TypeAttributes } from "rsuite/esm/@types/common";

export type AlertDialogType = "warning" | "danger" | "info";

const ColorMap: { [key: string]: TypeAttributes.Color | undefined } = {
  warning: "orange",
  danger: "red",
  info: "green",
};

export type AlertDialogControl = {
  open: () => void;
  close: () => void;
};

export type AlertDialogProps = {
  type?: AlertDialogType;
  message?: string;
  onSuccess?: Function;
  proceedBtnText?: string;
  body?: React.ReactNode;
};

const AlertDialogComponent = React.forwardRef<
  AlertDialogControl,
  AlertDialogProps
>((props, ref): JSX.Element => {
  const [open, setOpen] = React.useState(false);
  const openDialog = React.useCallback(() => setOpen(true), []);
  const closeDialog = React.useCallback(() => setOpen(false), []);
  React.useImperativeHandle(
    ref,
    () => ({
      open: openDialog,
      close: closeDialog,
    }),
    [openDialog, closeDialog]
  );
  return (
    <Modal open={open} backdrop="static" onClose={closeDialog}>
      <Modal.Body>
        <b>{props.message ?? "Are you sure you want to proceed?"}</b>
        {props.body && <div className="mt-3">{props.body}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button
          color={ColorMap[props.type ?? "info"]}
          onClick={() => {
            closeDialog();
            setTimeout(props.onSuccess?.(), 0);
          }}
          appearance="primary"
        >
          {props.proceedBtnText ?? "Ok"}
        </Button>
        <Button onClick={closeDialog} appearance="subtle">
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

export const AlertDialog = React.memo(AlertDialogComponent);
